import { Box, Text, Tooltip, Wrap, WrapItem } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

export interface FormulaFactor {
  /** Displayed value, e.g. "60 480€" */
  value: string;
  /** Tooltip explaining where this number comes from */
  tooltip: string;
  /** input = user-entered (blue), assumption = research constant (orange), derived = calculated (gray) */
  type: 'input' | 'assumption' | 'derived';
}

export interface FormulaProps {
  factors: FormulaFactor[];
  operator: '×' | '+';
  result: string;
  /** Use light colors for dark backgrounds (e.g. the highlighted total card) */
  onDark?: boolean;
}

const COLORS: Record<FormulaFactor['type'], string> = {
  input: 'blue.600',
  assumption: 'orange.600',
  derived: 'gray.600',
};

const COLORS_ON_DARK: Record<FormulaFactor['type'], string> = {
  input: 'blue.100',
  assumption: 'orange.200',
  derived: 'whiteAlpha.800',
};

const BORDER_COLORS: Record<FormulaFactor['type'], string> = {
  input: 'blue.300',
  assumption: 'orange.300',
  derived: 'gray.400',
};

const BORDER_COLORS_ON_DARK: Record<FormulaFactor['type'], string> = {
  input: 'blue.200',
  assumption: 'orange.300',
  derived: 'whiteAlpha.500',
};

export default function FormulaBreakdown({ factors, operator, result, onDark }: FormulaProps) {
  const colors = onDark ? COLORS_ON_DARK : COLORS;
  const borders = onDark ? BORDER_COLORS_ON_DARK : BORDER_COLORS;

  // Single open index — hover on desktop, tap on mobile, only one at a time
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const handleEnter = useCallback((i: number) => setOpenIdx(i), []);
  const handleLeave = useCallback(() => setOpenIdx(null), []);
  const handleTap = useCallback(
    (i: number) => setOpenIdx((prev) => (prev === i ? null : i)),
    [],
  );

  return (
    <Wrap spacing={1} align="center" justify="center" mt={3}>
      {factors.map((f, i) => (
        <WrapItem key={i} display="flex" alignItems="center">
          {i > 0 && (
            <Text fontSize="xs" color={onDark ? 'whiteAlpha.600' : 'gray.400'} mx={1} userSelect="none">
              {operator}
            </Text>
          )}
          <Tooltip
            label={f.tooltip}
            fontSize="xs"
            bg="gray.800"
            color="white"
            px={3}
            py={2}
            borderRadius="md"
            maxW="300px"
            hasArrow
            placement="top"
            isOpen={openIdx === i}
          >
            <Box
              as="span"
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={handleLeave}
              onClick={() => handleTap(i)}
              onBlur={handleLeave}
              tabIndex={0}
            >
              <Text
                as="span"
                fontSize="xs"
                fontWeight="600"
                color={colors[f.type]}
                borderBottom="1px dotted"
                borderColor={borders[f.type]}
                cursor="help"
                whiteSpace="nowrap"
              >
                {f.value}
              </Text>
            </Box>
          </Tooltip>
        </WrapItem>
      ))}
      <WrapItem display="flex" alignItems="center">
        <Text fontSize="xs" color={onDark ? 'white' : 'gray.500'} fontWeight="700" ml={1} whiteSpace="nowrap">
          = {result}
        </Text>
      </WrapItem>
    </Wrap>
  );
}
